// Pure branding resolver/patch-builder: field-level plant theme overrides, resolved independently
// against organization defaults and application defaults. No React, no redux, no fetch — every
// function here takes plain data in and returns plain data out, so the resolution rules (own-
// property presence, never `||`/truthiness) are easy to unit test in isolation.
import LynkIdlogo from "../LynkIdlogo.png";

export const LEAF_PATHS = ["theme.primaryColor", "theme.selectedTheme", "title", "logo", "favIcon", "statIcon.total", "statIcon.todayIn", "statIcon.todayOut", "statIcon.currentIn"];

export const ASSET_LEAVES = ["logo", "favIcon", "statIcon.total", "statIcon.todayIn", "statIcon.todayOut", "statIcon.currentIn"];

// `logo`/`favIcon` have a real application asset; the stat icons have none — their component
// supplies its own built-in inline icon when nothing overrides it (source stays "application").
export const APP_DEFAULTS = {
	theme: { primaryColor: "#ff7200", selectedTheme: "lightMode" },
	title: "LynkID",
	logo: LynkIdlogo,
	favIcon: "/favicon.ico",
	statIcon: { total: undefined, todayIn: undefined, todayOut: undefined, currentIn: undefined },
};

export function leafGet(obj, path) {
	return path.split(".").reduce((node, key) => (node && typeof node === "object" ? node[key] : undefined), obj);
}

export function leafHasOwn(obj, path) {
	const parts = path.split(".");
	let node = obj;
	for (const part of parts) {
		if (!node || typeof node !== "object" || !Object.prototype.hasOwnProperty.call(node, part)) return false;
		node = node[part];
	}
	return true;
}

export function normalizeColor(value) {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	return /^#[0-9a-fA-F]{6}$/.test(trimmed) ? trimmed.toLowerCase() : undefined;
}

export function normalizeMode(value) {
	return value === "lightMode" || value === "darkMode" ? value : undefined;
}

export function normalizeTitle(value) {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	return trimmed.length && trimmed.length <= 100 ? trimmed : undefined;
}

const SCALAR_NORMALIZERS = {
	"theme.primaryColor": normalizeColor,
	"theme.selectedTheme": normalizeMode,
	title: normalizeTitle,
};

function resolveScalarLeaf(path, plantOverrides, orgDefaults) {
	const normalize = SCALAR_NORMALIZERS[path];
	if (leafHasOwn(plantOverrides, path)) {
		const value = normalize(leafGet(plantOverrides, path));
		if (value !== undefined) return { value, source: "plant" };
	}
	if (leafHasOwn(orgDefaults, path)) {
		const value = normalize(leafGet(orgDefaults, path));
		if (value !== undefined) return { value, source: "organization" };
	}
	return { value: leafGet(APP_DEFAULTS, path), source: "application" };
}

// Asset leaves always resolve to `{ value: {url,fileName,expiresAt} | undefined, source, hidden }`.
// `hidden: true` means an explicit plant/org null: render nothing, never fall through further.
// `value: undefined` at the application level means "the consuming component supplies its own
// built-in fallback" (used for the three stat icons, which have no application-level asset).
function resolveAssetLeaf(path, plantOverrides, orgDefaults, now) {
	if (leafHasOwn(plantOverrides, path)) {
		const raw = leafGet(plantOverrides, path);
		if (raw === null) return { value: null, source: "plant", hidden: true };
		if (raw && typeof raw === "object") return { value: Date.parse(raw.expiresAt) <= now ? undefined : raw, source: "plant", hidden: false };
	}
	if (leafHasOwn(orgDefaults, path)) {
		const raw = leafGet(orgDefaults, path);
		if (raw && typeof raw === "object") return { value: Date.parse(raw.expiresAt) <= now ? undefined : raw, source: "organization", hidden: false };
	}
	const appDefault = leafGet(APP_DEFAULTS, path);
	return { value: appDefault === undefined ? undefined : { url: appDefault }, source: "application", hidden: false };
}

/**
 * Resolves every in-scope leaf independently against `plantOverrides` (a plant's raw `overrides`
 * object from `plant/get-theme`) and `orgDefaults` (the organization's raw URL-mode themeSettings).
 * Both default to `{}` so "no plant selected" / "org defaults not loaded yet" degrade to
 * organization/application defaults rather than throwing.
 */
export function resolveBranding(plantOverrides = {}, orgDefaults = {}, now = Date.now()) {
	return {
		theme: {
			primaryColor: resolveScalarLeaf("theme.primaryColor", plantOverrides, orgDefaults),
			selectedTheme: resolveScalarLeaf("theme.selectedTheme", plantOverrides, orgDefaults),
		},
		title: resolveScalarLeaf("title", plantOverrides, orgDefaults),
		logo: resolveAssetLeaf("logo", plantOverrides, orgDefaults, now),
		favIcon: resolveAssetLeaf("favIcon", plantOverrides, orgDefaults, now),
		statIcon: {
			total: resolveAssetLeaf("statIcon.total", plantOverrides, orgDefaults, now),
			todayIn: resolveAssetLeaf("statIcon.todayIn", plantOverrides, orgDefaults, now),
			todayOut: resolveAssetLeaf("statIcon.todayOut", plantOverrides, orgDefaults, now),
			currentIn: resolveAssetLeaf("statIcon.currentIn", plantOverrides, orgDefaults, now),
		},
	};
}

// --- Editor patch building ---------------------------------------------------

/**
 * Reads a browser File as the raw-base64 upload descriptor the backend contract expects
 * (`{fileName, contentType, base64}` with NO `data:...;base64,` prefix).
 */
export function fileToUploadDescriptor(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const dataUrl = String(reader.result || "");
			const commaIndex = dataUrl.indexOf(",");
			resolve({
				fileName: file.name,
				contentType: file.type,
				base64: commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : "",
			});
		};
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(file);
	});
}

/**
 * Builds the `{set, unset}` request body from a caller-tracked map of explicit pending operations
 * (never a diff of the displayed/merged object — untouched inherited fields must never enter
 * `set`, and reverting an edit back to its original value/source must remove its entry from
 * `pendingOps` before this is called).
 *
 * `pendingOps`: Map<leafPath, { op: "set", value } | { op: "unset" } | { op: "hide" }>
 * (`hide` is asset-only sugar for `{op:"set", value:null}`.)
 */
export function buildThemePatch(pendingOps) {
	const set = {};
	const unset = [];
	const entries = pendingOps instanceof Map ? pendingOps.entries() : Object.entries(pendingOps);
	for (const [path, entry] of entries) {
		if (!entry) continue;
		if (entry.op === "unset") {
			unset.push(path);
		} else if (entry.op === "hide") {
			set[path] = null;
		} else if (entry.op === "set") {
			set[path] = entry.value;
		}
	}
	return { set, unset };
}

export function isAssetLeaf(path) {
	return ASSET_LEAVES.includes(path);
}

// Which editor scopes may hide an asset (store an explicit `null`). Only a plant can hide an
// inherited image; at organization level `null` is rejected by the server — there is nothing
// above it to hide, and "unset" already means "fall back to the application default".
export function canHideInScope(scope) {
	return scope === "plant";
}
